import { ApiError } from '@/shared/api/api-error'
import { request } from '@/shared/api/request'
import { camelize } from '@/shared/lib/camelize'

/** Backend NotificationFlags - a [Flags] enum serialized as an Int bitmask. */
export const NOTIFICATION_FLAGS = {
  like: 1 << 0,
  comment: 1 << 1,
  follow: 1 << 2,
  newReferral: 1 << 3,
  reply: 1 << 4,
  mention: 1 << 5,
  giftReceived: 1 << 6,
  tokensEarned: 1 << 7,
  payoutReady: 1 << 8,
  push: 1 << 9,
  newPost: 1 << 10,
  message: 1 << 11,
} as const

export type NotificationFlag = keyof typeof NOTIFICATION_FLAGS

/** Backend default: everything on except Like. */
export const DEFAULT_NOTIFICATION_FLAGS = Object.entries(NOTIFICATION_FLAGS)
  .filter(([name]) => name !== 'like')
  .reduce((mask, [, bit]) => mask | bit, 0)

export function hasFlag(mask: number, flag: NotificationFlag): boolean {
  return (mask & NOTIFICATION_FLAGS[flag]) !== 0
}

export interface AppNotification {
  id: string
  type: number
  title: string
  body?: string | null
  actorId?: string | null
  actorName?: string | null
  actorAvatarUrl?: string | null
  date: string
  isRead: boolean
  data: Record<string, string>
}

export function notificationPostId(notification: AppNotification): string | null {
  return notification.data?.postId ?? notification.data?.post_id ?? null
}

/**
 * The point of interest a notification is about (event poi_comment_mention:
 * someone named the viewer in a comment on a place).
 */
export function notificationPoiId(notification: AppNotification): string | null {
  return notification.data?.poiId ?? notification.data?.poi_id ?? null
}

export function notificationConversationId(notification: AppNotification): string | null {
  return notification.data?.conversationId ?? notification.data?.conversation_id ?? null
}

/** Backend bits above the settings toggles: moderation notices and the community lifecycle. */
export const NOTIFICATION_SYSTEM_BIT = 1 << 12
export const NOTIFICATION_COMMUNITY_INVITE_BIT = 1 << 16

/** Page / community the notification is about (moderation notices, the community lifecycle, invites). */
export function notificationPageUsername(notification: AppNotification): string | null {
  return notification.data?.pageUsername ?? notification.data?.page_username ?? null
}

export function notificationCommunitySlug(notification: AppNotification): string | null {
  return (
    notification.data?.slug ?? notification.data?.communitySlug ?? notification.data?.community_slug ?? null
  )
}

export function notificationCommunityName(notification: AppNotification): string | null {
  return notification.data?.communityName ?? notification.data?.community_name ?? null
}

export function isCommunityInviteNotification(notification: AppNotification): boolean {
  return (notification.type & NOTIFICATION_COMMUNITY_INVITE_BIT) !== 0
}

/** L10n key (notifications namespace) of the modal's jump button for where this points. */
export function notificationDestinationLabelKey(notification: AppNotification) {
  if (notificationPostId(notification) != null) return 'goToPost' as const
  if (notificationPoiId(notification) != null) return 'goToPlace' as const
  if (notificationPageUsername(notification) != null) return 'goToPage' as const
  if (notificationCommunitySlug(notification) != null) return 'goToCommunity' as const
  return 'open' as const
}

const BASE = '/api/notifications'

export const notificationsApi = {
  /** `days` limits results to the last N days (server-side filter). */
  async getNotifications(
    limit = 50,
    offset = 0,
    days = 30,
  ): Promise<{ notifications: AppNotification[]; unreadCount: number }> {
    const data = await request<unknown>({ url: BASE, params: { limit, offset, days } })
    const page = camelize<{ notifications?: AppNotification[]; unreadCount?: number }>(data)
    return { notifications: page.notifications ?? [], unreadCount: page.unreadCount ?? 0 }
  },

  async getUnreadCount(): Promise<number> {
    try {
      const data = await request<Record<string, unknown>>({ url: `${BASE}/unread-count` })
      const value = data?.count ?? data?.unread_count ?? data?.unreadCount
      return typeof value === 'number' ? value : 0
    } catch {
      return 0
    }
  },

  async markRead(id: string): Promise<void> {
    try {
      await request({ method: 'POST', url: `${BASE}/${id}/read` })
    } catch {
      /* best-effort */
    }
  },

  async markAllRead(): Promise<void> {
    try {
      await request({ method: 'POST', url: `${BASE}/read-all` })
    } catch {
      /* best-effort */
    }
  },

  async deleteNotification(id: string): Promise<void> {
    await request({ method: 'DELETE', url: `${BASE}/${id}` })
  },

  /** 404 means the profile has no settings row yet → backend defaults apply. */
  async getSettings(): Promise<number> {
    try {
      const data = await request<Record<string, unknown>>({ url: `${BASE}/settings` })
      return typeof data?.flags === 'number' ? data.flags : DEFAULT_NOTIFICATION_FLAGS
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return DEFAULT_NOTIFICATION_FLAGS
      throw error
    }
  },

  async updateSettings(input: { add?: number; remove?: number }): Promise<void> {
    const body: Record<string, number> = {}
    if (input.add) body.add = input.add
    if (input.remove) body.remove = input.remove
    if (Object.keys(body).length === 0) return
    await request({ method: 'PUT', url: `${BASE}/settings`, data: body })
  },
}
